// Planet.tsx — A planet on a Kepler ellipse around its star, with Sebastian-Lague
// -style procedural terrain (per-fragment biome shading), atmosphere + ocean +
// cloud shells, and its moons. The body is floored to a minimum apparent size
// (1/3 the star's; visible at system scale, true scale up close); only the surface
// spins. Clicking the planet focuses the camera on it.
//
// LOD: the focused planet gets the high-detail mesh + ocean + clouds; planets seen
// across a system get a mid mesh and no shells; receded planets a coarse mesh.
//
// Eclipses are analytic (see shadow.ts): each frame the planet publishes its own
// world sphere (planetOcc, the umbra its moons fall into) and collects its moons'
// world spheres (moonOcc, the umbrae that fall on the planet, ocean, air, clouds).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Quaternion, Vector2, Vector3, Vector4 } from 'three';
import type { Group, Mesh } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { Planet as PlanetData, StarSystem } from '../types';
import { useGame } from '../state/context';
import { gameClock } from '../state/gameClock';
import { E2E_ENABLED, e2eRegisterBody } from '../e2e';
import { orbitPosition } from './orbit';
import { apparentScale, FLOOR_NONE, FLOOR_PLANET } from './apparentScale';
import { getPlanetGeometry, hashSeed } from './planet/terrain';
import { fetchPlanetClimate, hasSurfaceClimate } from './planet/climateRaster';
import type { ClimateRaster } from './planet/climateRaster';
import {
  buildWeatherTexture,
  fetchPlanetWeather,
  hasWeather,
  lerpWeatherInto,
} from './planet/weatherVideo';
import type { CloudVideo } from './planet/weatherVideo';
import { makeBiomeUniforms, makeCityUniforms, planetSurfacePatch } from './planet/surfaceShader';
import { PLANET_PRESETS } from './planet/presets';
import { planetCities, urbanization } from './planet/cities';
import { moonName, planetModel } from './planet/model';
import { makeShadowUniforms, setOccluders } from './shadow';
import type { Occluder } from './shadow';
import { Atmosphere } from './Atmosphere';
import { Ocean } from './Ocean';
import { Clouds } from './Clouds';
import { CloudShadows } from './CloudShadows';
import { Moon } from './Moon';
import { Rings } from './Rings';
import { Bracket } from './Bracket';
import { EntityLabel } from './EntityLabel';

interface Props {
  planet: PlanetData;
  /** The host system — its spectral class + this planet's orbit drive the climate
   *  (mean temperature) that selects the world's frozen/scorched surface treatment. */
  system: StarSystem;
  /** Star world position == the SystemDetail group's position (system.position). */
  starWorldPos: Vector3;
  /** Star core radius (penumbra size for the analytic eclipse). */
  starRadius: number;
}

// Deterministic axial tilt (obliquity, 0..1) from a hash of the planet id — like
// rotationHours in model.ts, but a distinct seed/bit-slice so a world's tilt and
// spin are independent. There is no axialTilt on the wire Planet, so we DERIVE it
// here (no Go-mirror / SCHEMA_VERSION bump). 0 = no tilt (sharp cold poles, large
// ice caps); 1 = extreme tilt (poles get a summer -> warmer, smaller caps). The
// shader (uObliq) flattens the latitudinal temperature contrast by this amount.
function obliquity01(id: string): number {
  let h = (0x811c9dc5 ^ 0x5bd1e995) >>> 0; // distinct seed from rotationHours' FNV basis
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Use the upper bits (rotationHours uses the low ones) so tilt != spin.
  return ((h >>> 13) & 1023) / 1023;
}

// Icosphere subdivision per attention tier. three.IcosahedronGeometry tessellates
// to 20*(detail+1)^2 triangles, so the focused planet — which fills the screen —
// gets a high subdivision (detail 128 ≈ 333k tris) for a near-photoreal close-up:
// smooth limb + fine, properly-lit terrain relief (terrain is displaced and normals
// computed per vertex, so mesh resolution IS the relief/lighting detail). Built once
// on focus (~300-500ms; the build blocks the main thread, so going much higher both
// stalls the fly-in and trips the playtest's 5s focus wait) then LRU-cached. Other
// planets stay mid; receded ones lo.
const DETAIL_HI = 128;
const DETAIL_MID = 4;
const DETAIL_LO = 2;

const TWO_PI = Math.PI * 2;

// Game-seconds for one full cycle of the baked cloud-motion loop. The loop replays the
// wind-advected clouds; this sets how fast it plays (motion scales with the sim-speed control, so
// at the higher speeds the player usually watches at, this reads as a natural drift). Tunable.
const CLOUD_LOOP_SECONDS = 4 * 3600;

export function Planet({ planet, system, starWorldPos, starRadius }: Props) {
  const { focus, focusedPlanetId, hover, hoveredId } = useGame();
  const [hovering, setHovering] = useState(false);
  const hovered = hovering || hoveredId === planet.id;
  const camera = useThree((state) => state.camera);
  // System mode (no planet focused) → every planet large; planet mode → only the
  // focused planet large, the rest recede.
  const focused = focusedPlanetId === planet.id;
  const large = focusedPlanetId === null || focused;
  const floor = large ? FLOOR_PLANET : FLOOR_NONE;
  const detail = focused ? DETAIL_HI : focusedPlanetId === null ? DETAIL_MID : DETAIL_LO;
  const ref = useRef<Group>(null);
  const bodyRef = useRef<Group>(null);
  const surfaceRef = useRef<Mesh>(null);
  const scratch = useMemo(() => new Vector3(), []);
  // First-frame game-time for the gas-giant flow, so its zonal sway / churn wind up
  // from a neutral state on focus rather than snapping in pre-sheared (mirrors
  // Clouds.tsx). Only the bounded sway/churn use it; the rigid spin uses absolute time.
  const gasStart = useRef<number | null>(null);

  // Under ?e2e=1, expose this planet's orbiting group (its origin is the body
  // centre, where the caret sits) so the playtest can read the planet's live
  // on-screen position as it orbits. Inert in production.
  useEffect(() => {
    if (!E2E_ENABLED) {
      return;
    }
    e2eRegisterBody(planet.id, ref.current);
    return () => e2eRegisterBody(planet.id, null);
  }, [planet.id]);

  const moons = planet.moons ?? [];
  // This planet's sphere (shadows its moons + rings); the moons' spheres (shadow
  // the planet surface, ocean, atmosphere, clouds). Refreshed each frame below.
  const planetOcc = useMemo<Occluder>(
    () => ({ pos: new Vector3(), radius: planet.radius }),
    [planet.radius],
  );
  const moonOcc = useMemo<Occluder[]>(
    () => moons.map(() => ({ pos: new Vector3(), radius: 0 })),
    [moons],
  );
  const model = useMemo(() => planetModel(planet), [planet]);
  // Climate inputs for the per-fragment surface shader: the day/night/mean temps the
  // sidebar shows, a derived obliquity, and the per-kind frost/scorch gates. The
  // shader builds a per-fragment temperature (latitude for rotators, substellar angle
  // for tidally-locked worlds) and places ice caps / vegetation / scorch from it.
  const { climate, tidalLock } = useMemo(() => {
    const cl = model.climate(system);
    const k = planet.kind;
    // Scorch (parching toward arid browns) only makes sense on worlds with soil/
    // vegetation — on lava/toxic/barren/icy worlds it would just tan their fixed
    // crust. Lava worlds never freeze, so they take no frost either.
    const noScorch =
      k === 'volcanic' || k === 'molten' || k === 'toxic' || k === 'gas' ||
      k === 'broken' || k === 'frozen' || k === 'barren_dry' || k === 'barren_cold';
    const noFrost = k === 'volcanic' || k === 'molten' || k === 'gas';
    return {
      climate: {
        meanTempC: cl.meanTempC,
        dayTempC: cl.dayTempC,
        nightTempC: cl.nightTempC,
        obliquity: obliquity01(planet.id),
        canFrost: !noFrost,
        canScorch: !noScorch,
      },
      tidalLock: planet.tidallyLocked ? 1 : 0,
    };
  }, [model, system, planet.tidallyLocked, planet.kind, planet.id]);

  const surfaceShadow = useMemo(makeShadowUniforms, []);
  const biomeUniforms = useMemo(
    () =>
      makeBiomeUniforms(planet.kind, planet.color, hashSeed(planet.id), climate, tidalLock),
    [planet.kind, planet.color, planet.id, climate, tidalLock],
  );
  // Coastal-city uniforms: a stable object whose .value fields are mutated by the
  // effect below (so the live shader updates without recompiling).
  const cityUniforms = useMemo(makeCityUniforms, []);
  const onBeforeCompile = useMemo(
    () => planetSurfacePatch(surfaceShadow, biomeUniforms, cityUniforms),
    [surfaceShadow, biomeUniforms, cityUniforms],
  );

  // The focused planet fetches its baked climate raster; once it arrives the terrain is
  // rebuilt on the SERVER's continents so the rendered coastline matches where the climate
  // maps say "ocean". Until then (and for non-focused / surfaceless bodies) the client-only
  // noise terrain is used. Each Planet instance owns one planet id, so this never crosses.
  const [raster, setRaster] = useState<ClimateRaster | null>(null);
  useEffect(() => {
    if (!focused || !hasSurfaceClimate(planet.kind)) {
      return;
    }
    let alive = true;
    fetchPlanetClimate(planet.id)
      .then((r) => {
        if (alive) setRaster(r);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [focused, planet.id]);

  // The focused planet also fetches its baked seasonal weather video (the 12-month cloud-cover
  // cycle); once it arrives Clouds lets the EBM's precipitation field place the cloud bands and
  // interpolates them by orbital phase. Only worth fetching for worlds that actually grow a deck.
  const [weather, setWeather] = useState<CloudVideo | null>(null);
  useEffect(() => {
    if (!focused || !model.hasClouds || !hasWeather(planet.kind)) {
      return;
    }
    let alive = true;
    fetchPlanetWeather(planet.id)
      .then((w) => {
        if (alive) setWeather(w);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [focused, model.hasClouds, planet.id, planet.kind]);

  // ONE weather texture for both the cloud deck and its shadows (built + replayed here), so the
  // shadows sample the exact same moving cloud field the deck renders and can't drift out of step.
  // The baked video is a seamless loop of wind-advected cloud motion; the frame loop below replays
  // it by a game-time phase (see CLOUD_LOOP_SECONDS).
  const weatherTex = useMemo(() => buildWeatherTexture(weather), [weather]);
  useEffect(() => () => weatherTex.dispose(), [weatherTex]);

  const { geometry, oceanGeometry } = useMemo(
    () =>
      getPlanetGeometry(
        hashSeed(planet.id),
        planet.kind,
        planet.radius,
        detail,
        focused ? (raster ?? undefined) : undefined,
      ),
    [planet.id, planet.kind, planet.radius, detail, focused, raster],
  );

  // Coastal cities for any inhabited world, at every LOD — so an inhabited planet's
  // night-side lights are visible in the system view, not only when focused. Placed
  // on THIS render mesh's shoreline (cities.ts reads its aElevation), re-derived when
  // the LOD changes so a city never lands in the sea as the focused mesh resolves a
  // sharper coast. Cheap: the coarse meshes have few vertices, the focused one is
  // already built. Fed to the shader uniforms below.
  const cities = useMemo(
    () =>
      planet.pops > 0
        ? planetCities(planet, geometry, {
            meanTempC: climate.meanTempC,
            dayTempC: climate.dayTempC,
            nightTempC: climate.nightTempC,
            obliquity: climate.obliquity,
            tidallyLocked: planet.tidallyLocked,
          })
        : [],
    [planet, geometry, climate],
  );
  useEffect(() => {
    cityUniforms.uCityCount.value = cities.length;
    // Global urbanization (ecumenopolis fill) for a megapopulous world.
    cityUniforms.uCityUrban.value = cities.length > 0 ? urbanization(planet.pops) : 0;
    const packed = cityUniforms.uCity.value as Vector4[];
    for (let i = 0; i < cities.length; i++) {
      packed[i].set(cities[i].dir[0], cities[i].dir[1], cities[i].dir[2], cities[i].angularRadius);
    }
  }, [cities, cityUniforms, planet.pops]);

  // World-space city directions (xyz) + sprawl radius (w) for the atmosphere's
  // night-limb city glow. The surface spins, so these are recomputed each frame from
  // the surface's live world orientation; a stable reusable array fed to <Atmosphere>.
  const cityWorld = useMemo(() => Array.from({ length: 50 }, () => new Vector4(1, 0, 0, 0)), []);
  const cityQuat = useMemo(() => new Quaternion(), []);

  const ocean = model.oceanTint;
  const emissive = model.emissive;
  // Free spinners turn once per their day length (model.climate); driven off
  // gameClock so a "day" matches the sidebar and stays slow at 1× rather than the
  // old arbitrary 0.4 rad/s whirl. Tidally-locked worlds instead track the star.
  const daySec = (model.dayLengthHours ?? 0) * 3600;

  useFrame((_state, delta) => {
    const g = ref.current;
    if (!g) {
      return;
    }
    // Gas giants fade in their fine streaks + storm vortices only on focus (high
    // LOD) so distant gas giants stay smooth bands and never shimmer, and drift
    // their zonal flow off the game clock so it speeds up with the simulation-speed
    // control, matching the planets' orbits and the cloud shell.
    if (planet.kind === 'gas') {
      const u = biomeUniforms.uGasDetail;
      const target = focused ? 1 : 0;
      u.value = (u.value as number) + (target - (u.value as number)) * Math.min(1, delta * 3);
      // Bounded gas-giant flow (see surfaceShader.ts uGasRot/uGasSway/uGasChurn). All
      // the precision-sensitive time reduction happens here in float64 so the shader
      // (float32) only ever sees small bounded values and never grains under fast-
      // forward — at 1,000,000× the game clock reaches millions of seconds within a few
      // wall-seconds, which an unbounded uTime fed into the rotation/fBm coords would
      // turn into sub-pixel shear striations + float32 precision speckle.
      const t = gameClock.time;
      // Rigid whole-deck zonal spin: wrap the angle mod 2π in float64 BEFORE the trig so
      // cos/sin never see a huge argument. 0.006 rad/s == the old mean jet rate.
      const rot = ((t * 0.006) % TWO_PI + TWO_PI) % TWO_PI;
      (biomeUniforms.uGasRot.value as Vector2).set(Math.cos(rot), Math.sin(rot));
      // Bounded differential-shear sway + bounded churn offsets, measured relative to
      // focus so they start neutral. Each sin() slope at t≈0 matches the old linear
      // ramp (GAS_SWAY_AMP=0.6 in-shader × 0.016667 == 0.010 rad/s; churn amplitudes ×
      // their rates == the old 0.018/0.013/0.009/0.008), so 1× looks unchanged, while
      // the amplitude bound (|sway|≤1, |churn|≤6) keeps the fBm coords small at any speed.
      gasStart.current ??= t;
      const tr = t - gasStart.current;
      biomeUniforms.uGasSway.value = Math.sin(tr * 0.016667);
      (biomeUniforms.uGasChurn.value as Vector4).set(
        6 * Math.sin(tr * (0.018 / 6)),
        -6 * Math.sin(tr * (0.013 / 6)),
        -6 * Math.sin(tr * (0.009 / 6)),
        6 * Math.sin(tr * (0.008 / 6)),
      );
    }
    orbitPosition(planet, gameClock.time, g.position);
    // Replay the baked cloud-MOTION loop (deck + shadows share this one texture): advance a
    // game-time phase through the wind-advected frames every frame so the clouds flow smoothly.
    // The loop is seamless, so the phase wraps without a jump. Motion scales with sim speed like
    // everything else on the game clock.
    if (weather) {
      const phase = (((gameClock.time / CLOUD_LOOP_SECONDS) % 1) + 1) % 1;
      lerpWeatherInto(weatherTex, weather, phase);
    }
    if (surfaceRef.current) {
      if (planet.tidallyLocked) {
        surfaceRef.current.rotation.y = Math.atan2(g.position.x, g.position.z);
      } else if (daySec > 0) {
        surfaceRef.current.rotation.y = (TWO_PI * (gameClock.time % daySec)) / daySec;
      }
    }
    g.getWorldPosition(scratch);
    planetOcc.pos.copy(scratch);
    if (bodyRef.current) {
      bodyRef.current.scale.setScalar(apparentScale(camera, scratch, planet.radius, floor));
    }
    // Rotate each city's object-space direction into WORLD space (the surface just
    // spun) so the atmosphere can glow at the night-side limb over coastal cities.
    if (cities.length > 0 && surfaceRef.current) {
      surfaceRef.current.updateWorldMatrix(true, false);
      surfaceRef.current.getWorldQuaternion(cityQuat);
      for (let i = 0; i < cities.length; i++) {
        const d = cities[i].dir;
        scratch.set(d[0], d[1], d[2]).applyQuaternion(cityQuat);
        const sprawl = Math.max(0.12, Math.min(0.3, cities[i].angularRadius * 3));
        cityWorld[i].set(scratch.x, scratch.y, scratch.z, sprawl);
      }
    }
    // Moons (children) refresh moonOcc in their own useFrame; the planet surface
    // reads it here (a frame stale at worst — imperceptible for an eclipse).
    setOccluders(surfaceShadow, starWorldPos, starRadius, moonOcc);
  });

  const onClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    focus(planet.id);
  };
  const onOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setHovering(true);
    hover(planet.id);
    document.body.style.cursor = 'pointer';
  };
  const onOut = () => {
    setHovering(false);
    hover(null);
    document.body.style.cursor = 'auto';
  };

  return (
    <group rotation={[planet.inclination, 0, 0]} visible={large}>
      <group ref={ref}>
        <group ref={bodyRef}>
          <mesh
            ref={surfaceRef}
            geometry={geometry}
            onClick={onClick}
            onPointerOver={onOver}
            onPointerOut={onOut}
          >
            <meshStandardMaterial
              roughness={model.surfaceRoughness}
              metalness={0.02}
              emissive={emissive?.color ?? '#000000'}
              emissiveIntensity={emissive?.intensity ?? 0}
              onBeforeCompile={onBeforeCompile}
            />
          </mesh>
          {focused && ocean && climate.meanTempC > -25 ? (
            <Ocean
              geometry={oceanGeometry}
              seaLevel={PLANET_PRESETS[planet.kind].ramp.seaLevel01}
              sunWorldPos={starWorldPos}
              sunRadius={starRadius}
              occluders={moonOcc}
              climate={climate}
              tidalLock={tidalLock}
              shallowColor={ocean.shallow}
              deepColor={ocean.deep}
            />
          ) : null}
          {focused && model.hasClouds ? (
            <>
              <CloudShadows
                planetRadius={planet.radius}
                sunWorldPos={starWorldPos}
                seed={hashSeed(planet.id)}
                coverage={model.cloudCover}
                weatherTex={weatherTex}
                hasWeather={!!weather}
                tidalLock={tidalLock}
              />
              <Clouds
                planetRadius={planet.radius}
                sunWorldPos={starWorldPos}
                sunRadius={starRadius}
                occluders={moonOcc}
                seed={hashSeed(planet.id)}
                color={model.cloudColor ?? '#ffffff'}
                coverage={model.cloudCover}
                climate={climate}
                tidalLock={tidalLock}
                weatherTex={weatherTex}
                hasWeather={!!weather}
              />
            </>
          ) : null}
          <Atmosphere
            planetRadius={planet.radius}
            sunWorldPos={starWorldPos}
            sunRadius={starRadius}
            occluders={moonOcc}
            kind={planet.kind}
            segments={focused ? 96 : 24}
            cityCount={cities.length}
            cityWorld={cityWorld}
          />
          {planet.rings ? (
            <Rings
              rings={planet.rings}
              sunWorldPos={starWorldPos}
              sunRadius={starRadius}
              occluder={planetOcc}
            />
          ) : null}
        </group>
        {moons.map((moon, i) => (
          <Moon
            key={moon.id}
            moon={moon}
            name={moonName(planet.name, i)}
            detail={focused ? DETAIL_MID : DETAIL_LO}
            planetFocused={focused}
            sunWorldPos={starWorldPos}
            sunRadius={starRadius}
            occluder={planetOcc}
            slot={moonOcc[i]}
          />
        ))}
        {hovered && focusedPlanetId === null ? <Bracket /> : null}
        {focusedPlanetId === null ? <EntityLabel text={planet.name} lit={hovered} /> : null}
      </group>
    </group>
  );
}
